import React, { Suspense, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import {
    TableRow,
    TableCell,
    Link,
    Box,
    styled,
    Typography,
    Button,
    useTheme,
} from '@mui/material';

import type { CommentTemplateWithAction } from 'models/CommentTemplate';

import useGeneralDialog from 'hooks/dialogs/useGeneralDialog';

import type { RowItems } from 'models/TableProps';

import { formatDate } from 'utils/dateFormatter';

import { Delete, Edit } from '@mui/icons-material';

import CommentTemplateDetails from './CommentTemplateDetails';
import { AddUpdateCommentTemplate } from 'components/comment-templates/AddUpdateCommentTemplateModal';

const StyledRow = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
}));

const StyledIcon = styled(Box)((props: { color?: string }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px',
    cursor: 'pointer',
    ':hover': {
        backgroundColor: props.color ?? '#ffdede',
        borderRadius: '50%',
    },
}));

const StyledLink = styled(Link)(() => ({
    cursor: 'pointer',
}));

const StyledBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: '12px',
}));

const CommentTemplateRow: React.FC<RowItems<CommentTemplateWithAction>> = (
    props
) => {
    const { row, index, forceRefresh } = props;

    const theme = useTheme();

    const { t } = useTranslation('commentTemplates');

    const labelId = `enhanced-table-checkbox-${index}`;

    const generalDialog = useGeneralDialog();

    const handleShowCommentTemplateDetails = useCallback((): void => {
        if (!CommentTemplateDetails) return;
        generalDialog.showDialog({
            title: row.title,
            component: () => (
                <Suspense>
                    <CommentTemplateDetails commentTemplate={row} />
                </Suspense>
            ),
        });
    }, [row]);

    const handleDelete = useCallback(
        (): void =>
            generalDialog.showDialog({
                title: t('DELETE_MODAL.TITLE'),
                hideActions: true,
                component: () => (
                    <Suspense>
                        <Typography variant="body2" align="justify">
                            {t('DELETE_MODAL.DESCRIPTION')}
                        </Typography>
                        <StyledBox>
                            <Button
                                variant="text"
                                onClick={generalDialog.closeDialog}
                            >
                                {t('DELETE_MODAL.CLOSE')}
                            </Button>
                            <Button
                                color="error"
                                variant="outlined"
                                onClick={async (): Promise<void> => {
                                    await row.deleteAction();
                                    generalDialog.closeDialog();
                                }}
                            >
                                {t('DELETE_MODAL.CONFIRM')}
                            </Button>
                        </StyledBox>
                    </Suspense>
                ),
            }),
        [generalDialog, row.deleteAction]
    );

    const handleOpenEdit = () => {
        generalDialog.showDialog({
            title: t('UPDATE_MODAL.TITLE'),
            confirmLabel: t('UPDATE_MODAL.CONFIRM'),
            closeLabel: t('UPDATE_MODAL.CLOSE'),
            component: AddUpdateCommentTemplate,
            preSetContent: { note: row.note, title: row.title, id: row.id },
            onCloseWithSuccess: (): void => {
                forceRefresh();
                generalDialog.closeDialog();
            },
        });
    };

    return (
        <TableRow hover>
            <TableCell component="th" id={labelId} align="left" scope="row">
                {row.id}
            </TableCell>

            <TableCell align="left">{row.title}</TableCell>

            <TableCell align="left">{formatDate(row.created_at)}</TableCell>

            <TableCell align='left'>{formatDate(row.updated_at)}</TableCell>

            <TableCell align="right">
                <StyledRow>
                    <StyledIcon
                        color={theme.palette.secondary['100']}
                        onClick={handleOpenEdit}
                    >
                        <Edit color="secondary" />
                    </StyledIcon>

                    <StyledIcon
                        color={theme.palette.error.light['100']}
                        onClick={handleDelete}
                    >
                        <Delete color="error" />
                    </StyledIcon>

                    <StyledLink onClick={handleShowCommentTemplateDetails}>
                        {t('TABLE_ROW.DETAILS')}
                    </StyledLink>
                </StyledRow>
            </TableCell>
        </TableRow>
    );
};

export default CommentTemplateRow;
